<template>
  <div class="amount-input-container">
    <h2 class="section-title">Enter Amount to Pay</h2>
    
    <!-- Priority Notice Banner -->
    <div v-if="isPriority" class="priority-notice">
      <i class="fas fa-star me-2"></i> Priority Transaction
    </div>
    
    <div class="amount-input-form">
      <div class="form-group">
        <label for="amountInput" class="form-label">Amount (PHP)</label>
        <div class="input-group mb-3">
          <span class="input-group-text">₱</span>
          <input
            type="text"
            class="form-control form-control-lg"
            id="amountInput"
            v-model="amount"
            placeholder="Enter amount to pay"
            :disabled="loading"
            @keyup.enter="confirmAmount"
            readonly
          />
        </div>
        <small class="form-text text-muted">Please enter the amount you need to pay</small>
      </div>
      
      <div v-if="error" class="alert alert-danger mt-3">
        {{ error }}
      </div>
      
      <div class="action-buttons mt-3">
        <button
          class="btn btn-primary confirm-btn w-100"
          type="button"
          @click="confirmAmount"
          :disabled="loading || !isValidAmount"
        >
          <span v-if="loading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          {{ loading ? 'Processing...' : 'Confirm Amount' }}
        </button>
      </div>
      
      <div class="virtual-keyboard">
        <div class="keyboard-row">
          <button class="key" @click="appendToAmount('1')">1</button>
          <button class="key" @click="appendToAmount('2')">2</button>
          <button class="key" @click="appendToAmount('3')">3</button>
        </div>
        <div class="keyboard-row">
          <button class="key" @click="appendToAmount('4')">4</button>
          <button class="key" @click="appendToAmount('5')">5</button>
          <button class="key" @click="appendToAmount('6')">6</button>
        </div>
        <div class="keyboard-row">
          <button class="key" @click="appendToAmount('7')">7</button>
          <button class="key" @click="appendToAmount('8')">8</button>
          <button class="key" @click="appendToAmount('9')">9</button>
        </div>
        <div class="keyboard-row">
          <button class="key key-clear" @click="clearAmount()">Clear</button>
          <button class="key" @click="appendToAmount('0')">0</button>
          <button class="key key-dot" @click="appendToAmount('.')">.</button>
        </div>
        <div class="keyboard-row">
          <button class="key key-delete" @click="deleteLastDigit()">
            <i class="fas fa-backspace"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'AmountInput',
  props: {
    config: {
      type: Object,
      default: () => ({})
    },
    transactionData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      amount: '',
      localError: null,
      loading: false
    };
  },
  computed: {
    ...mapGetters({
      storeLoading: 'kiosk/isLoading',
      storeError: 'kiosk/getError',
      isPriority: 'kiosk/isPriorityMode'
    }),
    error() {
      return this.localError || this.storeError;
    },
    isValidAmount() {
      // Check if amount is a valid number and greater than 0
      const numAmount = parseFloat(this.amount);
      return !isNaN(numAmount) && numAmount > 0 && /^\d+(\.\d{0,2})?$/.test(this.amount);
    }
  },
  created() {
    // Initialize amount from transactionData if available
    if (this.transactionData && this.transactionData.amount) {
      this.amount = this.transactionData.amount.toString();
    }
    
    // Check if we have the required data from previous steps
    this.$nextTick(() => {
      const store = this.$store;
      if (!store.getters['kiosk/getSelectedTransactionType']) {
        console.warn('No transaction type selected');
        this.$emit('step-back');
      }
    });
  },
  methods: {
    ...mapActions({
      setTransactionAmount: 'kiosk/setTransactionAmount'
    }),
    appendToAmount(digit) {
      // Only allow digits and one decimal point
      if (digit === '.' && this.amount.includes('.')) {
        return;
      }
      
      // Limit to 2 decimal places
      if (this.amount.includes('.') && this.amount.split('.')[1]?.length >= 2 && digit !== '.') {
        return;
      }
      
      this.amount += digit;
    },
    clearAmount() {
      this.amount = '';
    },
    deleteLastDigit() {
      this.amount = this.amount.slice(0, -1);
    },
    confirmAmount() {
      if (!this.isValidAmount) {
        this.localError = 'Please enter a valid amount';
        return;
      }
      
      try {
        this.localError = null;
        this.loading = true;
        
        // Store the amount in Vuex
        const amountValue = parseFloat(this.amount);
        this.setTransactionAmount(amountValue);
        
        // Emit step-complete event with amount data
        this.$emit('step-complete', {
          amount: amountValue
        });
      } catch (error) {
        console.error('Amount confirmation error:', error);
        this.localError = 'An error occurred while processing your request';
      } finally {
        this.loading = false;
      }
    },
    goBack() {
      this.$emit('step-back');
    }
  }
};
</script>

<style scoped>
.amount-input-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem;
  max-width: 700px;
  margin: 0 auto;
  height: 100%;
  scale: 1.5
}

.section-title {
  text-align: center;
  margin-bottom: 1.5rem;
  color: #2C3E50;
  font-size: 1.8rem;
  font-weight: 600;
}

.priority-notice {
  background-color: #28a745;
  color: white;
  padding: 0.8rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  width: 100%;
  text-align: center;
  font-size: 1.2rem;
  font-weight: 600;
  box-shadow: 0 4px 8px rgba(40, 167, 69, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}

.amount-input-form {
  width: 100%;
  max-width: 500px;
  display: flex;
  flex-direction: column;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.input-group-text {
  font-weight: bold;
  background-color: #f8f9fa;
  border-right: none;
}

.form-control-lg {
  font-size: 1.3rem;
  padding: 0.6rem 1rem;
  text-align: right;
  background-color: #ffffff;
  border-left: none;
}

.form-text {
  text-align: center;
}

.action-buttons {
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5rem;
}

.action-buttons .btn {
  padding: 0.5rem 1.5rem;
  font-size: 1.1rem;
  min-width: 120px;
}

.back-btn {
  background-color: #6c757d;
  color: white;
}

.confirm-btn {
  background-color: #4285f4;
  border-color: #4285f4;
}

.virtual-keyboard {
  width: 100%;
  max-width: 360px;
  margin: 0 auto;
  margin-top: 1rem;
}

.keyboard-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}

.key {
  width: 100px;
  height: 70px;
  margin: 0 5px;
  font-size: 1.5rem;
  border-radius: 4px;
  background-color: #ffffff;
  border: 1px solid #ced4da;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
}

.key:hover {
  background-color: #f8f9fa;
  box-shadow: 0 2px 5px rgba(0,0,0,0.15);
}

.key:active {
  background-color: #e9ecef;
  transform: translateY(1px);
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}

.key-clear {
  background-color: #ffc107;
  color: #212529;
  font-size: 1rem;
}

.key-delete {
  background-color: #6c757d;
  color: white;
  width: 100%;
}

.key-dot {
  font-weight: bold;
  font-size: 1.8rem;
}

/* Portrait mode adjustments */
@media screen and (orientation: portrait) {
  .amount-input-container {
    padding: 1.5rem;
    justify-content: center;
    height: 90vh;
  }
  
  .section-title {
    font-size: 2.2rem;
    margin-bottom: 2rem;
  }
  
  .priority-notice {
    font-size: 1.6rem;
    padding: 1.2rem;
    border-radius: 12px;
    margin-bottom: 2rem;
  }
  
  .form-control-lg {
    font-size: 1.8rem;
    padding: 1rem 1.5rem;
    height: auto;
  }
  
  .input-group-text {
    font-size: 1.8rem;
    padding: 0.5rem 1rem;
  }
  
  .form-text {
    font-size: 1.2rem;
  }
  
  .action-buttons .btn {
    font-size: 1.4rem;
    padding: 0.8rem 2rem;
    min-width: 150px;
  }
  
  .virtual-keyboard {
    margin-top: 2rem;
    max-width: 460px;
  }
  
  .keyboard-row {
    margin-bottom: 15px;
  }
  
  .key {
    width: 120px;
    height: 90px;
    font-size: 2rem;
    margin: 0 6px;
    border-radius: 12px;
  }
  
  .key-clear {
    font-size: 1.4rem;
  }
  
  .key-delete {
    font-size: 1.4rem;
  }
}
</style> 